import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import Button from 'antd/lib/button';
import {
	Col,
	Descriptions,
	Row,
} from 'antd';
import Select from 'antd/lib/select';
import Card, { CardTabListType } from 'antd/es/card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import {
	Currencies,
	FuelTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { formatCurrency } from '@shared/utils/currency';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	addFuelEuPenaltyToEstimate,
	deleteFuelEuCalculation,
	getFuelEuCalculations,
	updateFuelEuPenaltyToEstimate,
} from '@client/lib/api';
import NumericInput from '@client/components/NumericInput';
import StatisticCard from '@client/components/StatisticCard';
import styleColorPicker from '@client/screens/fleet/VoyageDetailsScreen/components/ProfitAndLossTab/utils/styleColorPicker';
import { useExchangeRates } from '@client/utils/hooks/useExchangeRates';
import styles from '../details/styles/FuelEuPenaltyCalculation.module.css';
import {
	calculateFuelEuData,
	FuelEuDataCalculation,
	transformCalculationToFuelEu,
	transformFuelEuToCalculation,
	yearOptions,
} from './fuelEuHelper';

const FuelEuCalculations = ({ estimate }: { estimate: GetEstimateDetailsResponse }) => {
	const hasInitializedFirstTab = useRef(false);
	const [isEditing, setIsEditing] = useState(false);
	const currentYear = new Date().getFullYear();
	const [
		fuelEuCalculations,
		refreshFuelEuCalculations,
	] = useFetchedState(async () => {
		const result = await getFuelEuCalculations(estimate.id);

		return result.sort((a, b) => Number(b.id) - Number(a.id)) ?? [];
	},
	[estimate.id]);

	const [selectedFuelGrade, setSelectedFuelGrade] =
		useState<Values<typeof FuelTypes>>(FuelTypes.HSFO);
	const [selectedConsumption, setSelectedConsumption] = useState<number>(0);
	const [selectedYear, setSelectedYear] = useState<number>(currentYear);
	const [currentTab, setCurrentTab] = useState<string | undefined>();
	const [relevantEntry, setRelevantEntry] = useState<FuelEuDataCalculation | null>();
	const exchangeRates = useExchangeRates(Currencies.EUR);
	const currencyConvertedPenalty = useMemo(() => {
		if (!exchangeRates || relevantEntry == null) {
			return 1;
		}

		return relevantEntry.penalty * exchangeRates[estimate.currency];
	}, [estimate.currency, exchangeRates, relevantEntry]);

	const addFuelEuPenaltyTab = async () => {
		const newEntry = await addFuelEuPenaltyToEstimate(
			estimate.id,
			`${selectedFuelGrade} (${currentYear})`,
		);

		const newTabId = newEntry.id.toString();
		setCurrentTab(newTabId);
		setSelectedFuelGrade(newEntry.fuelType);
		setSelectedConsumption(newEntry.consumption);
		setSelectedYear(newEntry.year);
		setRelevantEntry(transformFuelEuToCalculation(newEntry));

		await refreshFuelEuCalculations();
	};

	const getTabs = useCallback((): Array<CardTabListType> => {
		if (fuelEuCalculations == null) {
			return [];
		}

		return fuelEuCalculations.map((s) => {
			return ({
				label: `${s.name}`,
				key: s.id!.toString(),
				id: s.id!.toString(),
				closeable: fuelEuCalculations.length > 1,
				closeIcon: (
					fuelEuCalculations?.length > 1 && (
						<Button
							danger
							type="text"
							icon={(<FontAwesomeIcon icon={faTrash} />)}
						/>
					)
				),
			});
		});
	}, [fuelEuCalculations]);

	const handleTabChange = (tab: string) => {
		setCurrentTab(tab);

		const entry = fuelEuCalculations?.find((s) => s.id.toString() === tab);

		if (entry == null) {
			return;
		}

		setRelevantEntry(transformFuelEuToCalculation(entry));
		setSelectedFuelGrade(entry.fuelType);
		setSelectedConsumption(entry.consumption);
		setSelectedYear(entry.year);
		setIsEditing(false);
	};

	// Initialize first tab if no fuel eu data exists
	useEffect(() => {
		if (hasInitializedFirstTab.current) {
			return;
		}

		if (!fuelEuCalculations) {
			return;
		}

		if (fuelEuCalculations.length === 0) {
			hasInitializedFirstTab.current = true;
			addFuelEuPenaltyTab();

			return;
		}

		const currentExists = fuelEuCalculations.some((e) => e.id.toString() === currentTab);

		if (!currentExists) {
			const first = fuelEuCalculations[0];
			const newTab = first.id.toString();
			setCurrentTab(newTab);
			setSelectedFuelGrade(first.fuelType);
			setSelectedConsumption(first.consumption);
			setSelectedYear(first.year);
			setRelevantEntry(transformFuelEuToCalculation(first));
			hasInitializedFirstTab.current = true;
		}
	}, [fuelEuCalculations, currentTab]);

	useEffect(() => {
		if (!isEditing || relevantEntry?.id == null) {
			return;
		}

		const updatedName = `${selectedFuelGrade} (${selectedYear})`;
		const calc = calculateFuelEuData(
			relevantEntry.id,
			estimate.id,
			updatedName,
			selectedFuelGrade,
			selectedConsumption,
			selectedYear,
		);

		updateFuelEuPenaltyToEstimate(
			calc.id, estimate.id, transformCalculationToFuelEu(calc),
		).then(() => {
			refreshFuelEuCalculations();
		});
	}, [
		isEditing,
		estimate.id,
		relevantEntry?.id,
		selectedConsumption,
		selectedFuelGrade,
		selectedYear,
		refreshFuelEuCalculations,
	]);

	if (relevantEntry == null) {
		return null;
	}

	return (
		<Card
			onTabChange={handleTabChange}
			tabList={getTabs()}
			activeTabKey={currentTab}
			size="small"
			tabProps={
				{
					type: 'editable-card',
					onEdit: (targetKey, action) => {
						if (action === 'add') {
							addFuelEuPenaltyTab();
						} else if (action === 'remove' && typeof targetKey === 'string') {
							deleteFuelEuCalculation(estimate.id, Number(targetKey))
								.then(() => {
									// Optional: Reset tab if the one being deleted is the current one
									if (currentTab === targetKey) {
										const remaining =
											fuelEuCalculations?.filter((e) => e.id.toString() !== targetKey);
										const fallback = remaining?.[0];

										if (fallback) {
											setCurrentTab(fallback.id.toString());
											setSelectedFuelGrade(fallback.fuelType);
											setSelectedConsumption(fallback.consumption);
											setSelectedYear(fallback.year);
											setRelevantEntry(transformFuelEuToCalculation(fallback));
										}
									}

									refreshFuelEuCalculations();
								});
						}
					},
				}
			}
		>
			<Row justify="center" gutter={[16, 24]}>
				<Col span={4}>
					<div style={{ marginBottom: 8 }}>Fuel Grade</div>
					<Select
						placeholder="Select a grade"
						options={Object.values(FuelTypes).map((fuelGrade) => ({
							label: fuelGrade,
							value: fuelGrade,
						}))}
						value={selectedFuelGrade}
						onChange={(v) => {
							setSelectedFuelGrade(v as Values<typeof FuelTypes>);
							setIsEditing(true);

							if (relevantEntry?.id != null) {
								const updated = calculateFuelEuData(
									relevantEntry.id,
									estimate.id,
									`${v} (${selectedYear})`,
									v,
									selectedConsumption,
									selectedYear,
								);
								setRelevantEntry(updated);
							}
						}}
						defaultValue={FuelTypes.HSFO}
					/>
				</Col>
				<Col span={4}>
					<div style={{ marginBottom: 8 }}>Consumption</div>
					<NumericInput
						placeholder="MT"
						value={selectedConsumption}
						onChange={(v) => {
							setSelectedConsumption(v as number);
							setIsEditing(true);

							if (relevantEntry?.id != null) {
								const updated = calculateFuelEuData(
									relevantEntry.id,
									estimate.id,
									`${selectedFuelGrade} (${selectedYear})`,
									selectedFuelGrade,
									v as number,
									selectedYear,
								);
								setRelevantEntry(updated);
							}
						}}
						defaultValue={0}
					/>
				</Col>
				<Col span={4}>
					<div style={{ marginBottom: 8 }}>Year</div>
					<Select
						placeholder="Select a year"
						options={yearOptions}
						value={selectedYear}
						onChange={(v) => {
							const year = v ?? new Date().getFullYear();
							setSelectedYear(year);
							setIsEditing(true);

							if (relevantEntry?.id != null) {
								const updated = calculateFuelEuData(
									relevantEntry.id,
									estimate.id,
									`${selectedFuelGrade} (${year})`,
									selectedFuelGrade,
									selectedConsumption,
									year,
								);
								setRelevantEntry(updated);
							}
						}}
						defaultValue={currentYear}
					/>
				</Col>
				<Col span={12} />
			</Row>
			<Row>
				<Col span={12}>
					<Descriptions
						title="Well to Tank"
						bordered
						column={1}
						size="small"
						className={styles.fuelEuCalculation}
					>
						{Object.entries(relevantEntry?.wellToTank).map(([key, item]) => (
							<Descriptions.Item labelStyle={{ width: '50%' }} key={key} label={item.label}>
								<>
									<b>{item.displayValue}</b>
									{' '}
									{item.unit}
								</>
							</Descriptions.Item>
						))}
					</Descriptions>
					<Descriptions
						title="Tank to Wake"
						bordered
						column={1}
						size="small"
						className={styles.fuelEuCalculation}
					>
						{Object.entries(relevantEntry.tankToWake).map(([key, item]) => (
							<Descriptions.Item
								labelStyle={{ width: '50%' }}
								key={key}
								label={item.label}
							>
								<>
									<b>{item.displayValue}</b>
									{' '}
									{item.unit}
								</>
							</Descriptions.Item>
						))}
					</Descriptions>
					<Descriptions
						title="Slip"
						bordered
						column={1}
						size="small"
						className={styles.fuelEuCalculation}
					>
						{Object.entries(relevantEntry.cSlip).map(([key, item]) => (
							<Descriptions.Item
								labelStyle={{ width: '50%' }}
								key={key}
								label={item.label}
							>
								<>
									<b>{item.displayValue}</b>
									{' '}
									{item.unit}
								</>
							</Descriptions.Item>
						))}
					</Descriptions>
				</Col>
				<Col
					span={12}
				>
					<Descriptions
						title="Parameters"
						bordered
						column={1}
						size="small"
						className={styles.fuelEuCalculation}
					>
						{Object.entries(relevantEntry.parameters).map(([key, item]) => (
							<Descriptions.Item
								labelStyle={{ width: '50%' }}
								key={key}
								label={item.label}
							>
								<>
									<b>{item.displayValue}</b>
									{' '}
									{item.unit}
								</>
							</Descriptions.Item>
						))}
					</Descriptions>
					<Descriptions className={styles.fuelEuCalculationPenalty}>
						<Descriptions.Item>
							<StatisticCard
								items={[{
									label: `Fuel EU Penalty (${formatCurrency(
										Math.abs(relevantEntry.penalty),
										Currencies.EUR, { forceDecimals: true },
									)})`,
									children: formatCurrency(
										Math.abs(currencyConvertedPenalty),
										estimate.currency, { forceDecimals: true },
									),
									childColor: styleColorPicker(relevantEntry?.penalty, true).color,
								}]}
								customStyles={{
									margin: '0 auto',
								}}
							/>
						</Descriptions.Item>
					</Descriptions>

				</Col>
			</Row>
		</Card>
	);
};

export default FuelEuCalculations;
